import { useState } from 'react';
import useAffordance from 'Hooks/useAffordance';
import PanicError from 'Errors/Panic';
import { addQuery } from 'Helpers/Query';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';

const getOpenTabNames = (layoutResource) => {
  if (!layoutResource) {
    return [];
  }

  return layoutResource.attr('columns').resources().list().reduce((acc, currCol) => {
    return [
      ...acc,
      ...currCol.attr('tabs').resources().list().map(v => v.attr('name')),
    ];
  }, []);
};

const getOpenTabs = (layoutResource) => {
  if (!layoutResource) {
    return [];
  }

  const tabs = [];
  layoutResource.attr('columns').resources().list().forEach((column) => {
    column.attr('tabs').resources().list().forEach((tab) => {
      tabs.push({
        column: column.id(),
        name: tab.attr('name'),
      });
    });
  });

  return tabs;
};

export default function useCustomLayout(layoutLink) {
  const { rootNodeProps, logEvent } = useEventTrackingLogEvent();
  const [layoutResource, setLayoutResource] = useState(null);

  useAffordance(layoutLink, {
    onFetch: (resp) => {
      const [resource] = resp;

      setLayoutResource(resource);
    },
  });

  const setActiveTab = (colIdx, tabName) => {
    setLayoutResource((prevLayoutResource) => {
      const prevLayoutResourceCopy = prevLayoutResource.copy();

      prevLayoutResourceCopy
        .attr('columns')
        .resources()
        .index(colIdx)
        .attr('activeTabName', tabName);

      prevLayoutResourceCopy.submit();
      logEvent('Set Active Tab', { tabName });
      return prevLayoutResourceCopy;
    });
  };

  const closeTab = (tabName) => {
    setLayoutResource((prevLayoutResource) => {
      if (getOpenTabNames(prevLayoutResource).length === 1) {
        throw new PanicError('one-tab-required', 'Not allowed to close all tabs. At least one must remain open');
      }

      const prevLayoutResourceCopy = prevLayoutResource.copy();


      const numberOfColumnsBeforeClose = prevLayoutResourceCopy.attr('columns').resources().length();
      // Remove anything with a tab of that name
      prevLayoutResourceCopy.attr('columns').resources().list().forEach((columnResource) => {
        columnResource.attr('tabs').resources().filterItems(tabResource => tabResource.attr('name') !== tabName);
        columnResource.attr('activeTabName', (tabName === columnResource.attr('activeTabName')) ? '' : columnResource.attr('activeTabName'));
      });

      prevLayoutResourceCopy.attr('columns').resources().filterItems(columnResource => columnResource.attr('tabs').resources().length() > 0);
      const numberOfColumnsAfterClose = prevLayoutResourceCopy.attr('columns').resources().length();

      // if a column is closed as well, calculate column resize
      if (numberOfColumnsBeforeClose !== numberOfColumnsAfterClose) {
        const newColumnWidthPercent = (100 / prevLayoutResourceCopy.attr('columns').resources().list().length).toFixed(2);
        prevLayoutResourceCopy.attr('columns').resources().list().forEach((columnResource) => {
          columnResource.attr('widthPercent', newColumnWidthPercent);
        });
      }


      prevLayoutResourceCopy.submit();
      logEvent('Closed tab', { tabName });
      return prevLayoutResourceCopy;
    });
  };

  const openTab = (tabName, colIdx) => {
    setLayoutResource((prevLayoutResource) => {
      if (getOpenTabNames(prevLayoutResource).includes(tabName)) {
        throw new PanicError('tab-already-open', 'Tabs may not be duplicated', {
          tabName,
        });
      }

      const prevLayoutResourceCopy = prevLayoutResource.copy();

      const columnResource = prevLayoutResourceCopy.attr('columns').resources().index(colIdx);
      columnResource.attr('activeTabName', tabName);

      const tabsColl = columnResource.attr('tabs').create({ addToCollection: true });
      tabsColl.resources().last().attr('name', tabName);

      prevLayoutResourceCopy.submit();
      logEvent('Opened tab', { tabName });
      return prevLayoutResourceCopy;
    });
  };

  const moveTabToNewColumn = colIdx => (tabName) => {
    setLayoutResource((prevLayoutResource) => {
      const prevLayoutResourceCopy = prevLayoutResource.copy();
      const columnsColl = prevLayoutResourceCopy.attr('columns');

      // Remove anything with a tab of that name
      columnsColl.resources().list().forEach((columnResource) => {
        columnResource.attr('tabs').resources().filterItems(tabResource => tabResource.attr('name') !== tabName);
        columnResource.attr('activeTabName', (tabName === columnResource.attr('activeTabName')) ? '' : columnResource.attr('activeTabName'));
      });

      columnsColl.resources().filterItems(columnResource => columnResource.attr('tabs').resources().length() > 0);

      const isLastColumn = columnsColl.resources().length() === colIdx + 1;
      columnsColl.create({ addToCollection: true, addAtIndex: isLastColumn ? null : colIdx + 1 });

      const columnResource = columnsColl.resources().index(colIdx);
      const newColumnResource = columnsColl.resources().index(colIdx + 1);
      const tabsColl = newColumnResource.attr('tabs').create({ addToCollection: true });
      tabsColl.resources().last().attr('name', tabName);

      // Bisect original column width, and set width for both column resources
      const bisectedColumnWidth = (parseFloat(columnResource.attr('widthPercent')) / 2).toFixed(2);
      columnResource.attr('widthPercent', bisectedColumnWidth);
      newColumnResource.attr('widthPercent', bisectedColumnWidth);

      prevLayoutResourceCopy.submit();
      logEvent('Moved tab to new column', { colIdx, tabName });
      return prevLayoutResourceCopy;
    });
  };

  const moveTabToColumn = (prevColIdx, colIdx) => (tabName) => {
    setLayoutResource((prevLayoutResource) => {
      const prevLayoutResourceCopy = prevLayoutResource.copy();
      const columnsColl = prevLayoutResourceCopy.attr('columns');

      // Remove anything with a tab of that name
      columnsColl.resources().list().forEach((columnResource) => {
        columnResource.attr('tabs').resources().filterItems(tabResource => tabResource.attr('name') !== tabName);
        columnResource.attr('activeTabName', (tabName === columnResource.attr('activeTabName')) ? '' : columnResource.attr('activeTabName'));
      });

      const prevColumnResource = columnsColl.resources().index(prevColIdx);
      const columnResource = columnsColl.resources().index(colIdx);
      const tabsColl = columnResource.attr('tabs').create({ addToCollection: true });
      tabsColl.resources().last().attr('name', tabName);

      if (prevColumnResource.attr('tabs').resources().length() === 0) {
        const prevColumnWidth = parseFloat(prevColumnResource.attr('widthPercent'));
        const columnWidth = parseFloat(columnResource.attr('widthPercent'));
        columnResource.attr('widthPercent', (prevColumnWidth + columnWidth).toFixed(2));
      }

      // Remove anything that doesn't have any tabs
      columnsColl.resources().filterItems(colResource => colResource.attr('tabs').resources().length() > 0);

      prevLayoutResourceCopy.submit();
      logEvent('Moved tab to column', { prevColIdx, colIdx, tabName });
      return prevLayoutResourceCopy;
    });
  };

  const resizeColumnSave = (colIdx, newWidthPercent) => {
    setLayoutResource((prevLayoutResource) => {
      const prevLayoutResourceCopy = prevLayoutResource.copy();
      const columnsColl = prevLayoutResourceCopy.attr('columns');
      const columnResource = columnsColl.resources().index(colIdx);
      const prevColumnWidthPercent = parseFloat(columnResource.attr('widthPercent'));
      const neighborColumnResource = columnsColl.resources().index(colIdx - 1);
      const prevNeighborColumnWidthPercent = parseFloat(neighborColumnResource.attr('widthPercent'));
      const newNeighborWidthPercent = ((prevColumnWidthPercent + prevNeighborColumnWidthPercent) - newWidthPercent).toFixed(2);

      columnResource.attr('widthPercent', newWidthPercent.toFixed(2));
      neighborColumnResource.attr('widthPercent', newNeighborWidthPercent);

      prevLayoutResourceCopy.submit();
      logEvent('Resized column', {
        colIdx,
        newWidthPercent: Math.round(newWidthPercent),
      });
      return prevLayoutResourceCopy;
    });
  };

  const popOutTab = (tabName) => {
    logEvent('Popped out tab', { tabName });
    addQuery(
      { poppedOutTab: tabName },
    );
  };

  const dragTab = (tabName, colIdx, tabIdx) => {
    setLayoutResource((prevLayoutResource) => {
      const prevLayoutResourceCopy = prevLayoutResource.copy();
      const columnsColl = prevLayoutResourceCopy.attr('columns');
      const numberOfColumnsBeforeClose = prevLayoutResourceCopy.attr('columns').resources().length();

      // Remove anything with a tab of that name
      columnsColl.resources().list().forEach((columnResource) => {
        columnResource.attr('tabs').resources().filterItems(tabResource => tabResource.attr('name') !== tabName);
        columnResource.attr('activeTabName', (tabName === columnResource.attr('activeTabName')) ? '' : columnResource.attr('activeTabName'));
      });

      // Handle 'remove column by dragging' edge case
      let newColIndex = colIdx;
      columnsColl.resources().filterItems((columnResource, idx) => {
        if (idx < colIdx && columnResource.attr('tabs').resources().length() === 0) {
          newColIndex -= 1;
        }
        return columnResource.attr('tabs').resources().length() > 0;
      });
      const columnResource = columnsColl.resources().index(newColIndex);

      // Calculate new tab index
      const isLastTab = columnResource.attr('tabs').resources().length() === tabIdx;
      const newTabColl = columnResource.attr('tabs').create({ addToCollection: true, addAtIndex: isLastTab ? null : tabIdx });
      newTabColl.resources().index(tabIdx).attr('name', tabName);


      // TODO: we should calculate the width of the columns relative to the user's preset
      // Recalculate column sizes
      const numberOfColumnsAfterClose = prevLayoutResourceCopy.attr('columns').resources().length();
      if (numberOfColumnsBeforeClose !== numberOfColumnsAfterClose) {
        const newColumnWidthPercent = (100 / prevLayoutResourceCopy.attr('columns').resources().list().length).toFixed(2);
        prevLayoutResourceCopy.attr('columns').resources().list().forEach((colres) => {
          colres.attr('widthPercent', newColumnWidthPercent);
        });
      }

      prevLayoutResourceCopy
        .attr('columns')
        .resources()
        .index(newColIndex)
        .attr('activeTabName', tabName);

      prevLayoutResourceCopy.submit();
      logEvent('Dragged tab', { tabName, colIdx, tabIdx });
      return prevLayoutResourceCopy;
    });
  };

  return {
    rootNodeProps,
    closeTab,
    dragTab,
    layoutResource,
    moveTabToNewColumn,
    moveTabToColumn,
    openTab,
    openTabNames: getOpenTabNames(layoutResource),
    setActiveTab,
    resizeColumnSave,
    popOutTab,
    openTabs: getOpenTabs(layoutResource),
  };
}
